/**
 * Resources Fragment authorizes against, and the actions defined on each.
 * The Permissions RFC is the source of truth for this inventory.
 *
 * Actions are resource-specific (not generic CRUD) so they map cleanly onto
 * real GraphQL operations and REST endpoints. The inventory is intentionally
 * coarse: `ledger:update` covers `updateLedger`, `updateLedgerAccount`, and
 * `updateLedgerEntry`. An action splits only when a real authorization
 * boundary emerges between operations that share a verb.
 *
 * Adding a resource or action requires an RFC update plus a matching entry
 * in `ROLE_PERMISSIONS`.
 */

declare const __permission: unique symbol;

/**
 * A `<resource>:<action>` string carrying a phantom brand. The brand is
 * type-only — erased at runtime, so a branded value is the plain string — but
 * it makes the union nominal: a bare literal like `'ledger:post_entry'` is not
 * assignable to `Permission`, so every permission must be referenced through
 * the `Permissions` catalog below. This is what gives editors a navigable
 * symbol and prevents stray literals.
 */
type Branded<S extends string> = S & { readonly [__permission]: S };

// The single point where the brand is attached. `perm` infers the literal `S`,
// so each leaf keeps its precise `<resource>:<action>` type.
const perm = <S extends string>(s: S): Branded<S> => s as Branded<S>;

/**
 * The permission catalog and source of truth. Each leaf is the full
 * `<resource>:<action>` permission; reference permissions as
 * `Permissions.ledger.post_entry` rather than as string literals.
 */
export const Permissions = {
  schema: {
    store: perm('schema:store'),
    delete: perm('schema:delete'),
    read: perm('schema:read'),
  },
  ledger: {
    create: perm('ledger:create'),
    update: perm('ledger:update'),
    delete: perm('ledger:delete'),
    read: perm('ledger:read'),
    post_entry: perm('ledger:post_entry'),
  },
  link: {
    create: perm('link:create'),
    update: perm('link:update'),
    delete: perm('link:delete'),
    read: perm('link:read'),
    sync: perm('link:sync'),
  },
  workspace: {
    update: perm('workspace:update'),
    read: perm('workspace:read'),
  },
  workspace_member: {
    invite: perm('workspace_member:invite'),
    delete: perm('workspace_member:delete'),
    read: perm('workspace_member:read'),
    update: perm('workspace_member:update'),
  },
  api_client: {
    create: perm('api_client:create'),
    delete: perm('api_client:delete'),
    update: perm('api_client:update'),
    read: perm('api_client:read'),
  },
  mcp_client: {
    create: perm('mcp_client:create'),
    delete: perm('mcp_client:delete'),
    read: perm('mcp_client:read'),
  },
  data_export: {
    create: perm('data_export:create'),
    delete: perm('data_export:delete'),
    read: perm('data_export:read'),
  },
  custom_currency: {
    create: perm('custom_currency:create'),
    read: perm('custom_currency:read'),
  },
  payment: {
    create: perm('payment:create'),
    read: perm('payment:read'),
  },
  payment_account: {
    verify: perm('payment_account:verify'),
  },
  payment_method: {
    create: perm('payment_method:create'),
    delete: perm('payment_method:delete'),
    read: perm('payment_method:read'),
  },
  // Used for experimental features when you're not sure yet which permissions to use.
  experimental: {
    experimental: perm('experimental:experimental'),
  },
  user: {
    create: perm('user:create'),
    read: perm('user:read'),
    invite: perm('user:invite'),
  },
} as const;

export type Resource = keyof typeof Permissions;

export type Action<R extends Resource = Resource> =
  keyof (typeof Permissions)[R];

/**
 * The full set of `<resource>:<action>` permission strings — the union of every
 * leaf in `Permissions`. Branded, so each resource is paired only with the
 * actions that resource defines and bare literals are rejected: `Permission`
 * cannot include cross-resource combinations like `'ledger:invite'` (`invite`
 * exists only on `workspace_member`) or `'workspace:post_entry'` (`post_entry`
 * exists only on `ledger`).
 */
export type Permission = {
  [R in Resource]: (typeof Permissions)[R][keyof (typeof Permissions)[R]];
}[Resource];

/**
 * The unbranded `<resource>:<action>` union. Use this — not `Permission` — as a
 * `Record` key type: a branded `Permission` value cannot index a
 * `Record<Permission, T>` (the brand isn't a valid property key), but it is
 * assignable to `PermissionKey`, so `Record<PermissionKey, T>` indexes cleanly.
 */
export type PermissionKey = {
  [R in Resource]: `${R & string}:${Action<R> & string}`;
}[Resource];

/**
 * Recovers the plain `<resource>:<action>` literal from a single branded
 * `Permission`. The brand stores the literal so it survives extraction (a
 * template-literal `infer` would widen the branded intersection to `string`).
 * Needed wherever a branded permission must become a `Record` key.
 */
export type UnbrandPermission<P extends Permission> = P extends {
  readonly [__permission]: infer L extends string;
}
  ? L
  : never;

/**
 * Runtime enumeration of every `Permission`. Iterate this rather than
 * re-deriving the union when building a `Record<Permission, T>`.
 */
export const PERMISSIONS: readonly Permission[] = Object.values(
  Permissions
).flatMap((actions) => Object.values(actions));

/**
 * The actions defined on each resource, as arrays. Derived from `Permissions`
 * for consumers that want the per-resource action list rather than the full
 * permission strings.
 */
// `Object.keys` widens each action list to `string[]`, so bridge through
// `unknown` to recover the precise per-resource action types.
export const RESOURCE_ACTIONS = Object.fromEntries(
  Object.entries(Permissions).map(([resource, actions]) => [
    resource,
    Object.keys(actions),
  ])
) as unknown as { [R in Resource]: readonly Action<R>[] };

// Compile error if any leaf drifts from its keys — a mistyped value like
// `ledger: { post_entry: perm('ledger:post_enrty') }` fails this assignment.
const _leavesMatchKeys: {
  [R in Resource]: { [A in Action<R>]: Branded<`${R & string}:${A & string}`> };
} = Permissions;
